import React, { Component, createRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Tooltip } from 'antd';
import {
    ArrowLeft,
    ArrowRight,
    CircleAlert,
    CircleCheck,
    Circle,
    ExternalLink,
    Eye,
    EyeOff,
    Info,
    KeyRound,
    LifeBuoy,
    Lock,
    LogIn,
    Mail,
    MailCheck,
    TriangleAlert
} from 'lucide-react';

import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import { AuthLayout } from '../common';
import { AuthBrandMark } from '../common/layout/AuthLayout';
import ForgotPasswordController from './forgotPassword.controller';
import OtpInput from './elements/OtpInput';
import StepProgress from './elements/StepProgress';
import { CODE_LENGTH, MESSAGES, PASSWORD_MAX, PASSWORD_MIN, formatCountdown, maskEmail, passwordChecks } from './forgotPassword.helpers';

const STEPS = ['email', 'code', 'password'];

const HEADINGS = {
    email: { title: '¿Olvidaste tu contraseña?', subtitle: `Ingresa tu correo y te enviaremos un código de ${CODE_LENGTH} dígitos para crear una nueva.` },
    code: { title: 'Revisa tu correo', subtitle: 'Si el correo está registrado, te enviamos un código de verificación.' },
    password: { title: 'Crea una nueva contraseña', subtitle: 'Elige una contraseña que no hayas usado antes en Shelf.' },
    done: { title: 'Tu contraseña fue cambiada', subtitle: 'Ya puedes iniciar sesión con tu nueva contraseña.' }
};

/** Inline error under a field or step (announced by screen readers). */
function FieldError({ id, children }) {
    if (!children) return null;
    return (
        <p id={id} role="alert" className="m-0 mt-2 flex items-start gap-1.5 text-sm text-danger-fg">
            <CircleAlert className="mt-[3px] shrink-0 text-[14px]" />
            <span>{children}</span>
        </p>
    );
}

/** A rule of the new password: grey until met, then green. */
function Rule({ met, children }) {
    return (
        <li className={`flex items-center gap-1.5 text-xs transition-colors ${met ? 'text-success-fg' : 'text-surface-500'}`}>
            {met ? <CircleCheck className="shrink-0 text-[14px]" /> : <Circle className="shrink-0 text-[14px] text-surface-300" />}
            <span>{children}</span>
        </li>
    );
}

class ShelfForgotPassword extends Inherit(ForgotPasswordController, Component) {
    state = {
        step: 'email', // 'email' | 'code' | 'password' | 'done'
        email: '',
        code: '',
        password: '',
        confirm: '',
        passwordVisible: false,
        loading: false,
        resending: false,
        resendIn: 0,
        expiresMinutes: 15,
        // The email's company (company/session.js shape), in memory only.
        company: null,
        // Inline error of the current step
        error: null,
        // Why the user is back at step 1 (expired code, too many attempts)
        notice: null,
        // The email belongs to a company another admin serves: { companyName, url }
        elsewhere: null,
        // Step 3 was submitted once: show unmet rules as errors
        submitted: false,
        // The error of step 2 is about the code itself (red boxes), not the connection
        codeRejected: false
    };

    emailRef = createRef();
    codeRef = createRef();
    passwordRef = createRef();
    confirmRef = createRef();
    doneRef = createRef();

    renderNotice() {
        const { notice } = this.state;
        if (!notice) return null;
        return (
            <div role="alert" className="mb-5 flex items-start gap-2.5 rounded-lg border border-warning-border bg-warning-bg px-3.5 py-3 text-sm text-warning-fg">
                <TriangleAlert className="mt-[3px] shrink-0 text-[15px]" />
                <div>
                    <div className="font-medium">{notice}</div>
                    <div className="mt-0.5">Presiona «Enviar código» para recibir uno nuevo.</div>
                </div>
            </div>
        );
    }

    renderElsewhere() {
        const { elsewhere } = this.state;
        if (!elsewhere) return null;
        const name = elsewhere.companyName || 'otra empresa';
        return (
            <div role="status" className="flex items-start gap-2.5 rounded-lg border border-highlight-border bg-highlight-bg px-3.5 py-3 text-sm text-highlight-text">
                <Info className="mt-[3px] shrink-0 text-[15px]" />
                <div className="min-w-0">
                    <div>
                        Tu cuenta pertenece a <span className="font-medium">{name}</span>. Recupera tu contraseña desde su panel administrativo.
                    </div>
                    {elsewhere.url && (
                        <a href={elsewhere.url} className="mt-1.5 inline-flex items-center gap-1 font-medium text-primary-600 hover:text-primary-700">
                            Ir al panel de {name}
                            <ExternalLink className="text-[13px]" />
                        </a>
                    )}
                </div>
            </div>
        );
    }

    renderEmailStep() {
        const { email, loading, error } = this.state;
        return (
            <>
                {this.renderNotice()}
                <div>
                    <label htmlFor="forgot-email" className="mb-1.5 block text-sm font-medium text-surface-700">
                        Correo electrónico
                    </label>
                    <Input
                        id="forgot-email"
                        ref={this.emailRef}
                        size="large"
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        value={email}
                        disabled={loading}
                        status={error ? 'error' : undefined}
                        aria-invalid={Boolean(error) || undefined}
                        aria-describedby={error ? 'forgot-email-error' : undefined}
                        onChange={(e) => this.setEmail(e.target.value)}
                        placeholder="tu@empresa.com"
                        prefix={<Mail className="text-surface-400" />}
                    />
                    <FieldError id="forgot-email-error">{error}</FieldError>
                </div>
                {this.renderElsewhere()}
                <Button type="primary" htmlType="submit" size="large" block loading={loading} icon={<ArrowRight />} iconPosition="end" className="!mt-6">
                    Enviar código
                </Button>
            </>
        );
    }

    renderCodeStep() {
        const { email, code, loading, resending, resendIn, expiresMinutes, error, codeRejected } = this.state;
        return (
            <>
                <div className="mb-5 flex items-start gap-3 rounded-lg border border-highlight-border bg-highlight-bg px-3.5 py-3 text-sm text-highlight-text">
                    <MailCheck className="mt-[2px] shrink-0 text-[16px]" />
                    <p className="m-0 min-w-0">
                        Enviamos un código de {CODE_LENGTH} dígitos a <span className="break-words font-semibold">{maskEmail(email)}</span>. Es válido por{' '}
                        {expiresMinutes} minutos. Si no lo ves, revisa tu carpeta de spam.
                    </p>
                </div>

                <div>
                    <div id="forgot-code-label" className="mb-1.5 text-sm font-medium text-surface-700">
                        Código de verificación
                    </div>
                    <OtpInput
                        ref={this.codeRef}
                        value={code}
                        disabled={loading}
                        invalid={Boolean(error) && codeRejected}
                        labelledBy="forgot-code-label"
                        describedBy={error ? 'forgot-code-error' : undefined}
                        onChange={(value) => this.setCode(value)}
                        onComplete={(value) => this.verifyCode(value)}
                    />
                    <FieldError id="forgot-code-error">{error}</FieldError>
                </div>

                <Button type="primary" htmlType="submit" size="large" block loading={loading} icon={<ArrowRight />} iconPosition="end" className="!mt-6">
                    Verificar
                </Button>

                <div className="!mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm">
                    <div className="flex items-center gap-1.5 text-surface-500">
                        <span className="hidden sm:inline">¿No te llegó?</span>
                        {resendIn > 0 ? (
                            <span aria-live="off" className="tabular-nums text-surface-400">
                                Reenviar en {formatCountdown(resendIn)}
                            </span>
                        ) : (
                            <Button type="link" size="small" className="!h-auto !p-0 font-medium" loading={resending} disabled={loading} onClick={() => this.resendCode()}>
                                Reenviar código
                            </Button>
                        )}
                    </div>
                    <Button type="link" size="small" className="!h-auto !p-0 font-medium" disabled={loading || resending} onClick={() => this.changeEmail()}>
                        Cambiar correo
                    </Button>
                </div>
            </>
        );
    }

    renderPasswordToggle() {
        const { passwordVisible } = this.state;
        const label = passwordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña';
        return (
            <Tooltip title={label}>
                <button
                    type="button"
                    aria-label={label}
                    aria-pressed={passwordVisible}
                    className="-mr-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-[16px] text-surface-400 transition-colors hover:bg-surface-100 hover:text-surface-600"
                    onClick={() => this.togglePasswordVisibility()}>
                    {passwordVisible ? <Eye /> : <EyeOff />}
                </button>
            </Tooltip>
        );
    }

    renderPasswordStep() {
        const { email, password, confirm, passwordVisible, loading, error, submitted } = this.state;
        const checks = passwordChecks(password, confirm);
        const type = passwordVisible ? 'text' : 'password';
        const passwordError = submitted && !checks.longEnough ? MESSAGES.PASSWORD_SHORT : null;
        // Mismatch shows once the confirmation is as long as the password, or on submit.
        const mismatch = confirm && password !== confirm && (submitted || confirm.length >= password.length) ? MESSAGES.PASSWORD_MISMATCH : null;
        const confirmError = mismatch || (submitted && !confirm ? 'Confirma tu nueva contraseña.' : null);

        return (
            <>
                {/* Lets password managers save the new password for this email. */}
                <input type="email" name="username" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true" className="sr-only" />

                <div>
                    <label htmlFor="forgot-password" className="mb-1.5 block text-sm font-medium text-surface-700">
                        Nueva contraseña
                    </label>
                    <Input
                        id="forgot-password"
                        ref={this.passwordRef}
                        size="large"
                        type={type}
                        autoComplete="new-password"
                        maxLength={PASSWORD_MAX}
                        value={password}
                        disabled={loading}
                        status={passwordError || error ? 'error' : undefined}
                        aria-invalid={Boolean(passwordError || error) || undefined}
                        aria-describedby="forgot-password-rules forgot-password-error"
                        placeholder="••••••••"
                        onChange={(e) => this.setPassword(e.target.value)}
                        prefix={<Lock className="text-surface-400" />}
                        suffix={this.renderPasswordToggle()}
                    />
                    <ul id="forgot-password-rules" className="m-0 mt-2 list-none p-0">
                        <Rule met={checks.longEnough}>Al menos {PASSWORD_MIN} caracteres</Rule>
                    </ul>
                    <FieldError id="forgot-password-error">{passwordError || error}</FieldError>
                </div>

                <div>
                    <label htmlFor="forgot-confirm" className="mb-1.5 block text-sm font-medium text-surface-700">
                        Confirmar contraseña
                    </label>
                    <Input
                        id="forgot-confirm"
                        ref={this.confirmRef}
                        size="large"
                        type={type}
                        autoComplete="new-password"
                        maxLength={PASSWORD_MAX}
                        value={confirm}
                        disabled={loading}
                        status={confirmError ? 'error' : undefined}
                        aria-invalid={Boolean(confirmError) || undefined}
                        aria-describedby={confirmError ? 'forgot-confirm-error' : undefined}
                        placeholder="••••••••"
                        onChange={(e) => this.setConfirm(e.target.value)}
                        prefix={<Lock className="text-surface-400" />}
                    />
                    <FieldError id="forgot-confirm-error">{confirmError}</FieldError>
                </div>

                <Button type="primary" htmlType="submit" size="large" block loading={loading} icon={<KeyRound />} iconPosition="end" className="!mt-6">
                    Cambiar contraseña
                </Button>
            </>
        );
    }

    renderDone() {
        return (
            <div>
                <Button ref={this.doneRef} type="primary" size="large" block icon={<LogIn />} iconPosition="end" onClick={() => this.goToLogin()}>
                    Iniciar sesión
                </Button>
            </div>
        );
    }

    renderStep() {
        switch (this.state.step) {
            case 'code':
                return this.renderCodeStep();
            case 'password':
                return this.renderPasswordStep();
            case 'done':
                return this.renderDone();
            default:
                return this.renderEmailStep();
        }
    }

    render() {
        const { step, email } = this.state;
        const done = step === 'done';
        const heading = HEADINGS[step];

        return (
            <AuthLayout>
                <div className="w-full max-w-[400px]">
                    {this.props.contextHolder}

                    <div className="shelf-card p-6 sm:p-8">
                        <div className={done ? 'mb-7 text-center' : 'mb-7'}>
                            {done ? (
                                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-bg text-[28px] text-success-fg ring-8 ring-success-bg/40 animate-scale-in">
                                    <CircleCheck />
                                </span>
                            ) : (
                                <div className="flex items-center justify-between gap-3">
                                    <AuthBrandMark size="lg" />
                                    <StepProgress current={STEPS.indexOf(step)} total={STEPS.length} />
                                </div>
                            )}
                            <h1 className="m-0 mt-5 text-2xl font-semibold tracking-tight text-surface-900" aria-live="polite">
                                {heading.title}
                            </h1>
                            <p className="m-0 mt-1.5 text-sm text-surface-500">{heading.subtitle}</p>
                        </div>

                        <form key={step} noValidate onSubmit={(e) => this.submit(e)} className="space-y-4 animate-fade-in">
                            {this.renderStep()}
                        </form>

                        <div className="mt-7 border-t border-surface-100 pt-5 text-center">
                            {!done && (
                                <Link
                                    to="/login"
                                    state={email ? { email } : undefined}
                                    className="inline-flex items-center gap-1.5 text-sm font-medium text-surface-600 transition-colors hover:text-primary-600">
                                    <ArrowLeft className="text-[14px]" />
                                    Volver a iniciar sesión
                                </Link>
                            )}
                            <p className={`m-0 text-xs text-surface-400 ${done ? '' : 'mt-4'}`}>© {new Date().getFullYear()} Shelf. Todos los derechos reservados.</p>
                        </div>
                    </div>

                    <div className="mt-5 flex items-center justify-center gap-1.5 text-sm text-surface-500">
                        <LifeBuoy className="text-surface-400" />
                        <span>¿Necesitas ayuda?</span>
                        <a href="/support" className="font-medium text-primary-600 transition-colors hover:text-primary-700">
                            Contáctanos
                        </a>
                    </div>
                </div>
            </AuthLayout>
        );
    }
}

export default withNavigate(ShelfForgotPassword);
